/**
 * CanvasCursors - Custom cursor overlays for canvas tools
 * Extracted from BaseCanvas.tsx to reduce component complexity
 */

import React from 'react'
import { PaintBucket, Pencil, Trash2 } from 'lucide-react'
import { CURSOR_Z_INDEX, TOOL_ADD_PLAYER, TOOL_DRAW, TOOL_ERASE, TOOL_FILL } from '@/constants'
import { DEFAULT_PLAYER_COLOR } from '@/constants/Formation.constants'
import type { DrawingState } from '@/types'

interface CanvasCursorsProps {
	drawingState: DrawingState
	cursorPosition: { x: number; y: number } | null
	isOverCanvas: boolean
	isHoveringDeletable: boolean
	shouldShowCustomCursor: boolean
	playerCursorDiameter: number
}

/**
 * Renders custom cursor overlays based on active tool
 * - Pencil for draw tool
 * - Paint bucket for fill tool
 * - Circle for erase tool
 * - Player circle for add player tool
 */
export function CanvasCursors({
								  drawingState,
								  cursorPosition,
								  isOverCanvas,
								  isHoveringDeletable,
								  shouldShowCustomCursor,
								  playerCursorDiameter,
							  }: CanvasCursorsProps) {
	if (!isOverCanvas || !cursorPosition || !shouldShowCustomCursor) {
		return null
	}

	const cursorStyle = {
		left: cursorPosition.x,
		top: cursorPosition.y,
		zIndex: CURSOR_Z_INDEX,
	}

	return (
		<>
			{/* Custom Pencil Cursor - only visible when draw tool is active */}
			{drawingState.tool === TOOL_DRAW && (
				<div className="canvas-cursor-pencil" style={cursorStyle}>
					<Pencil size={24} />
				</div>
			)}

			{/* Custom Fill Cursor - paint bucket icon */}
			{drawingState.tool === TOOL_FILL && (
				<div className="canvas-cursor-fill" style={cursorStyle}>
					<PaintBucket size={24} />
				</div>
			)}

			{/* Custom Eraser Cursor */}
			{drawingState.tool === TOOL_ERASE && !isHoveringDeletable && (
				<div className="canvas-cursor-eraser" style={cursorStyle}>
					<div
						className="canvas-eraser-circle"
						style={{
							width: `${drawingState.eraseSize}px`,
							height: `${drawingState.eraseSize}px`,
						}}
					/>
				</div>
			)}

			{/* Erase tool hovering over deletable item - red trash icon */}
			{drawingState.tool === TOOL_ERASE && isHoveringDeletable && (
				<div className="canvas-cursor-erase-delete" style={cursorStyle}>
					<Trash2 size={20} />
				</div>
			)}

			{/* Custom Add Player Cursor - Player circle preview */}
			{drawingState.tool === TOOL_ADD_PLAYER && (
				<div className="canvas-cursor-add-player" style={cursorStyle}>
					<div
						className="canvas-add-player-circle"
						style={{
							width: `${playerCursorDiameter}px`,
							height: `${playerCursorDiameter}px`,
							backgroundColor: DEFAULT_PLAYER_COLOR,
						}}
					/>
				</div>
			)}
		</>
	)
}
